import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Plus, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

import ImageCropperModal from './ImageCropperModal';
import { Crop } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TunersTab({ teams, onRefresh, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingTuner, setEditingTuner] = useState(null);
  const [formData, setFormData] = useState({ name: '', price: 50000, image_url: '', description: '', history: '' });
  const [uploading, setUploading] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      setUploading(true);
      toast.loading('Uploading image...');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      
      const imageUrl = response.data.url;
      setCropperImage(imageUrl);
      toast.success('Image uploaded! Crop it now or close to use as-is.');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTuner) {
        const tunerId = editingTuner.team_id || editingTuner.tuner_id;
        await axios.put(`${BACKEND_URL}/api/tuners/${tunerId}`, formData, { withCredentials: true });
        toast.success('Tuner updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/tuners`, formData, { withCredentials: true });
        toast.success('Tuner created');
      }
      setIsOpen(false);
      setEditingTuner(null);
      setFormData({ name: '', price: 50000, image_url: '', description: '', history: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to save tuner');
    }
  };

  const openEdit = (tuner) => {
    setEditingTuner(tuner);
    setFormData({ name: tuner.name, price: tuner.price || 50000, image_url: tuner.image_url || '', description: tuner.description || '', history: tuner.history || '' });
    setIsOpen(true);
  };

  return (
    <>
    <div className="bg-card border border-zinc-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-2xl">Tuners Management</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-team-button"
              onClick={() => { setEditingTuner(null); setFormData({ name: '', price: 50000, image_url: '', description: '', history: '' }); }}
              className="btn-skew bg-primary text-white px-6 py-2 font-bold uppercase flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Tuner</span>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border border-zinc-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingTuner ? 'Edit Tuner' : 'Add Tuner'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Name</label>
                <input type="text" data-testid="team-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Price (Credits: 30K-80K)</label>
                <input type="number" data-testid="team-price-input" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Image</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input type="text" data-testid="team-image-input" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Enter URL or upload file" className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" />
                    <label className={`flex items-center gap-2 px-4 h-12 cursor-pointer transition-colors ${uploading ? 'bg-zinc-700' : 'bg-primary hover:bg-primary/80'} text-white`}>
                      <Upload size={18} />
                      <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                  </div>
                  {formData.image_url && (
                    <div className="flex items-center gap-2">
                      <img src={formData.image_url} alt="Preview" className="w-16 h-16 object-cover border border-zinc-700" />
                      <button type="button" onClick={() => setCropperImage(formData.image_url)} className="flex items-center gap-1 text-primary hover:text-primary/80 text-sm"><Crop size={14} /> Crop</button>
                      <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} className="text-red-500 hover:text-red-400 text-sm">Remove</button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description (optional)</label>
                <textarea data-testid="tuner-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 text-white min-h-[80px]" placeholder="Brief description about the tuner team..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">History (optional)</label>
                <textarea data-testid="tuner-history-input" value={formData.history} onChange={(e) => setFormData({ ...formData, history: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 text-white min-h-[80px]" placeholder="Team history, championships, notable achievements..." />
              </div>
              <button type="submit" data-testid="save-team-button" className="w-full bg-primary text-white py-3 font-bold uppercase">
                {editingTuner ? 'Update' : 'Create'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">NAME</th>
              <th className="text-right p-4 font-heading font-bold text-sm text-zinc-400">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((tuner) => {
              const tunerId = tuner.team_id || tuner.tuner_id;
              return (
                <tr key={tunerId} className="border-b border-zinc-800 hover:bg-zinc-900">
                  <td className="p-4 font-bold">{tuner.name}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(tuner)} className="text-secondary hover:text-white mr-3">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => onDelete(tunerId)} className="text-red-500 hover:text-red-400">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

      {cropperImage && (
        <ImageCropperModal
          imageSrc={cropperImage}
          onCropped={async (url) => {
            setFormData(prev => ({ ...prev, image_url: url }));
            setCropperImage(null);
            if (editingTuner) {
              try {
                await axios.put(`${BACKEND_URL}/api/tuners/${editingTuner.tuner_id}`, { ...formData, image_url: url }, { withCredentials: true });
                toast.success('Tuner photo updated!');
                onRefresh();
              } catch { toast.error('Photo cropped but failed to save tuner'); }
            }
          }}
          onClose={() => { setFormData(prev => prev.image_url ? prev : { ...prev, image_url: cropperImage }); setCropperImage(null); }}
        />
      )}
    </>
  );
}